<template>
  <div :class="_fs_class">
    <slot></slot>

    <div class="view-document">
      <a :id="`view-document_${id}`" :title="title" :href="url">
        <i class="icon-doc-text-1"></i>
        <span class="file-name">
          {{ documentName }}
          <span v-if="showMetadata && fileMetadataDisplay">
            ({{ fileMetadataDisplay }})
          </span>
        </span>
      </a>
    </div>
    <cat-btn
      v-if="!readonly"
      class="cat-view-document-remove"
      :hint="removeButtonTitle"
      :link="true"
      :negative="true"
      :right="true"
      icon="times-light"
      :label="removeButtonLabel"
      :offscreenLabel="documentName"
      @ui-click="_removeClick"
      >{{ removeButtonLabel }}</cat-btn
    >
    <slot name="after-input"></slot>
    <cat-errors v-if="errors" :errors="errors" />
    <span class="flex-full-width text-size-small view-document-date">
      {{ documentUploadDate }}
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatBtn from "@/components/catBtn/catBtn.vue";
import CatErrors from "@/components/catErrors/catErrors.vue";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);

    return {
      resolveTxt
    };
  },
  name: "CatViewDocument",
  components: {
    CatBtn,
    CatErrors
  },
  props: {
    id: {
      type: [String, Number],
      description: "Document upload ID"
    },
    errors: {
      type: Array,
      description: "List of errors"
    },
    readonly: {
      type: Boolean,
      description: "Shows document upload in read-only mode."
    },
    mandatory: {
      type: Boolean,
      default: false,
      description: "Indicates if the field is mandatory."
    },
    documentName: {
      type: [String],
      default: "Download document",
      description: "Display the file name that has been uploaded"
    },
    documentUploadDate: {
      type: [String],
      default: null,
      description: "Represents the date and time when a document was uploaded."
    },
    title: {
      type: [String],
      default: "Download document",
      description:
        "Used as the title attribute within an anchor tag, to provide additional information or context for a document name label"
    },
    removeButtonLabel: {
      type: [String],
      default: "Remove",
      description:
        "Text displayed on the button or control that allows users to remove or delete an uploaded file or item"
    },
    removeButtonTitle: {
      type: [String],
      default: "Remove Document",
      description:
        "Used as the title attribute within an anchor tag, providing additional information when users interact with a button that removes an uploaded file."
    },
    url: {
      type: String,
      required: true,
      description: "External link to the uploaded file"
    },
    documentType: {
      type: String,
      default: null,
      description: "Formatted file type (e.g., 'PDF', 'DOCX')"
    },
    documentSize: {
      type: String,
      default: null,
      description: "Formatted file size (e.g., '2.5 MB')"
    },
    showMetadata: {
      type: Boolean,
      default: true,
      description:
        "Controls whether file metadata (type and size) should be displayed"
    }
  },
  computed: {
    _fs_class: function () {
      return {
        "cat-view-document": true,
        invalid: this.errors && this.errors.length > 0,
        required: this.mandatory,
        optional: !this.mandatory
      };
    },
    fileMetadataDisplay: function () {
      const parts = [];
      if (this.documentType && this.documentType.trim()) {
        parts.push(this.documentType);
      }
      if (this.documentSize && this.documentSize.trim()) {
        parts.push(this.documentSize);
      }
      return parts.join(" ");
    }
  },
  methods: {
    _removeClick: function (event) {
      this.$emit("ui-remove-document", {
        event: event,
        id: this.id ? this.id : null
      });
    }
  }
});
</script>
